/**
 * Phase 105-Agent-C — NotificationToast hook.
 *
 * Reuses the existing `sonner` toast lib already wired in the app (no
 * new dep). Watches the customer-side `order.status` for transitions
 * between known Phase-C lifecycle markers and fires a 4-second toast
 * once per transition.
 *
 * The previous status is held in a ref so the toast doesn't fire on
 * the initial mount or on identical re-fetches.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

const LIFECYCLE_TOAST = {
  assigned:         { tone: "info",    msg: "Your delivery partner has been assigned." },
  pickup_assigned:  { tone: "info",    msg: "Your delivery partner has been assigned." },
  accepted:         { tone: "info",    msg: "Your partner accepted the task and is en route." },
  picked_up:        { tone: "success", msg: "Parcel picked up. Tracking live." },
  picked:           { tone: "success", msg: "Parcel picked up. Tracking live." },
  in_transit:       { tone: "info",    msg: "Your order is out for delivery." },
  out_for_delivery: { tone: "info",    msg: "Your order is out for delivery." },
  delivered:        { tone: "success", msg: "Delivered. Thanks for using PickNGo!" },
};

export default function useLifecycleToasts(order) {
  const prevStatusRef = useRef(null);
  const prevTidRef = useRef(null);

  useEffect(() => {
    const tid = order?.tracking_id;
    const status = String(order?.status || "").toLowerCase();
    if (!tid || !status) return;

    // First observation for this tracking_id — remember + skip.
    if (prevTidRef.current !== tid) {
      prevTidRef.current = tid;
      prevStatusRef.current = status;
      return;
    }

    if (status !== prevStatusRef.current && LIFECYCLE_TOAST[status]) {
      const { tone, msg } = LIFECYCLE_TOAST[status];
      const opts = { duration: 4000, id: `lifecycle-${tid}-${status}`,
                     description: `Order ${tid}` };
      if (tone === "success") toast.success(msg, opts);
      else                    toast.info(msg, opts);
    }
    prevStatusRef.current = status;
  }, [order?.tracking_id, order?.status]);
}
